import styles from './BugReportModal.module.css';
import * as Sentry from '@sentry/react';

import useAuth from '@modules/AuthModule/hooks/useAuth';
import {
  Button,
  ButtonGroup,
  Headline,
  Modal,
  Stack,
  TextArea,
} from '@servicetitan/design-system';
import DateTime from '@lib/utils/datetime';

import { ModalSizes } from '@servicetitan/design-system/dist/components/Modal/components';
import { useEffect, useState } from 'react';
import { useScreenshotContext } from '@components/ScreenshotProvider/useScreenshotContext';
import useService from '@modules/ServiceModule/useService';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import { useDiagnostics } from '@modules/DiagnosticsModule/DiagnosticsContext';
import appManager from '@app/appManager';
import FullStoryModule from '@modules/FullStoryModule';
import environment from '@lib/environment';
import { useFeatureFlags } from '@modules/FeatureFlagModule';
import ToastModule from '@modules/ToastModule';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';

type Props = {
  setOpen: (open: boolean) => void;
};

const BugReportModal: React.FC<Props> = ({ setOpen }) => {
  const auth = useAuth();
  const phoneState = usePhoneState();
  const service = useService();
  const tasks = useTasks();
  const ff = useFeatureFlags();
  const { errors, events } = useDiagnostics();
  const { image, takeScreenshotOfElement } = useScreenshotContext();
  const [message, setMessage] = useState('');
  const [capturedData, setCapturedData] = useState<any>({});
  const logs = appManager.logs.latest;

  useEffect(() => {
    setCapturedData({
      date: new Date().toISOString(),
      env: environment.RUNTIME_ENV,
      mode: environment.MODE,
      errors,
      fullStory: FullStoryModule.sessionUrl,
      auth,
      phoneState,
      service,
      tasks,
      featureFlags: ff,
      events,
      logs,
    });
    takeScreenshotOfElement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleClose() {
    setOpen(false);
  }

  function base64ToUint8Array(base64String: string) {
    const binaryString = atob(base64String);
    const uint8Array = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      uint8Array[i] = binaryString.charCodeAt(i);
    }
    return uint8Array;
  }

  const getUser = () => {
    return (
      auth.user || {
        name: 'Unknown',
        org: {
          id: '',
          name: 'Unknown',
        },
        contactCenterId: '',
        accountId: '',
      }
    );
  };

  const handleSubmit = async () => {
    const user = getUser();
    const fileData = JSON.stringify(capturedData, undefined, 2);
    const dt = DateTime(new Date()).format('YYYYMMDDhhmmss');
    const userNameEncoded = encodeURI(auth.user?.name ?? 'Unknown');
    const userFeedback = {
      message,
      name: user.name,
    };
    Sentry.captureFeedback(userFeedback, {
      captureContext: {
        tags: {
          'org.name': user.org.name,
          'org.id': user.org.id,
          contactCenterId: user.contactCenterId,
          accountId: user.accountId,
        },
      },
      attachments: [
        {
          filename: `contactcenter-data-${userNameEncoded}-${dt}.json`,
          data: fileData,
          contentType: 'application/json',
        },
        {
          filename: `contactcenter-screenshot-${userNameEncoded}-${dt}.png`,
          data: base64ToUint8Array(image.replace('data:image/png;base64,', '')),
        },
      ],
    });
    setMessage('');
    setOpen(false);
    ToastModule.showToast({
      title: `Bug report was submitted successfully`,
      status: 'success',
      duration: 5000,
    });
  };

  const disabled = !message;

  return (
    <Modal.Window open={true} size={ModalSizes.S}>
      <Stack
        className='Modal__header p-x-4 p-y-3'
        alignItems='center'
        justifyContent='space-between'
      >
        <Headline className='m-b-0'>Report a Bug</Headline>
      </Stack>
      <Modal.Content className={styles.bugReportModalContent}>
        <Stack direction='column' spacing={2}>
          <div>Briefly describe what went wrong...</div>
          <TextArea
            placeholder='Type your message'
            value={message}
            className={styles.messageTextArea}
            autoHeight
            maxRows={5}
            rows={3}
            onChange={(e) => setMessage(e.target.value)}
          />
          <div>
            The following data will be submitted, along with a screenshot, to
            ServiceTitan in this bug report.
          </div>
          <div className={styles.bugReportModalData}>
            <pre>{JSON.stringify(capturedData, undefined, 2)}</pre>
          </div>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <ButtonGroup>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={disabled}>
            Submit
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal.Window>
  );
};

export default BugReportModal;
